<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { DxButton } from 'devextreme-vue/button';
import { BottoPagePanel, showError } from '@botto/core';
import { getDirectionsLookup } from '../../lookups';
import { getSmppClientsLookup } from '../../api/adapters/accounts';
import { createGroupsAdapter } from '../../api';
import type { StreamGroup } from '../../api';
import RoutingChoicesMasterDetailGrid from './RoutingChoicesMasterDetailGrid.vue';

const route = useRoute();
const router = useRouter();

const streamId = computed(() => {
  const n = Number(route.params.streamId);
  return Number.isFinite(n) ? n : null;
});

const groupId = computed(() => {
  const n = Number(route.params.groupId);
  return Number.isFinite(n) ? n : null;
});

const loading = ref(false);
const group = ref<StreamGroup | null>(null);
const choicesGridRef = ref<InstanceType<typeof RoutingChoicesMasterDetailGrid> | null>(null);

const smppClientsLookup = getSmppClientsLookup();
const directionsLookup = getDirectionsLookup();

const pageTitle = computed(() => {
  const parts: string[] = [];
  if (group.value?.stream_id ?? streamId.value) {
    const cl = smppClientsLookup.labelOf(group.value?.stream_id ?? streamId.value);
    if (cl) parts.push(cl);
  }
  if (group.value?.direction_id) {
    const dir = directionsLookup.labelOf(group.value.direction_id);
    if (dir) parts.push(`Направление: ${dir}`);
  }
  if (group.value?.name) parts.push(group.value.name);
  if (parts.length) return parts.join(' · ');
  return groupId.value != null
    ? `Распределение трафика #${groupId.value}`
    : 'Распределение трафика';
});

async function loadGroup() {
  if (streamId.value == null || groupId.value == null) return;
  loading.value = true;
  try {
    const adapter = createGroupsAdapter(streamId.value);
    group.value = (await adapter.byKey?.(groupId.value)) ?? null;
  } catch (e) {
    showError(e);
  } finally {
    loading.value = false;
  }
}

function goBack() {
  if (streamId.value != null && groupId.value != null) {
    void router.push(`/sms/routing-choices/${streamId.value}/${groupId.value}`);
  } else {
    void router.push({
      path: '/sms/routing-choices',
      query: streamId.value != null ? { streamId: String(streamId.value) } : undefined,
    });
  }
}

watch([streamId, groupId], () => {
  void loadGroup();
}, { immediate: true });

onMounted(async () => {
  await Promise.all([
    smppClientsLookup.ensureLoaded(),
    directionsLookup.ensureLoaded(),
  ]);
});
</script>

<template>
  <BottoPagePanel :title="pageTitle" standalone>
    <template #actions>
      <DxButton text="К группе" styling-mode="outlined" @click="goBack" />
    </template>

    <div v-if="loading" class="rc-traffic-status">Загрузка…</div>
    <div v-else-if="groupId == null" class="rc-traffic-status">Не указана группа</div>
    <RoutingChoicesMasterDetailGrid
      v-else
      ref="choicesGridRef"
      :group-id="groupId"
    />
  </BottoPagePanel>
</template>

<style scoped>
.rc-traffic-status {
  padding: 24px;
  color: var(--dx-color-text-secondary, #666);
}

:deep(#routing-choices-grid) {
  height: calc(100vh - 220px);
}
</style>
